// BasketFacet.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BasketFacet.module.css';
import { useCCContext, Basket } from '../CC/CCContext';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import GenericCheckbox from '../GenericComponents/GenericCheckbox';
import * as ccUtils from '../CC/utils';

interface BasketFacetProps {
  contentHeight: string;
  sort?: 'alpha' | 'count';
}

interface BasketFacetItem {
  basketId: string;
  basketName: string;
  count: number; // Number of items in basket
  created: string;
  altered: string;
}

interface CopyBasketResponse {
  basketId?: string;
  error?: string;
}

const BasketFacet: React.FC<BasketFacetProps> = ({
  contentHeight,
  sort = 'alpha' // Changed default sort to 'alpha'
}) => {
  const { t } = useTranslation();
  const ctx = useCCContext();
  const { user, setUser } = useCCContext();
  const { cc, setCc, setFirst, setSelectedRecord } = useCCContext();
  const [basketData, setBasketData] = useState<BasketFacetItem[]>([]);
  const [userInput, setUserInput] = useState('');
  const [currentSort, setCurrentSort] = useState<'alpha' | 'count'>(sort);
  const facetRootRef = useRef<HTMLDivElement>(null);

  // Filter baskets based on user input
  const filteredBaskets = basketData.filter(basket =>
    basket.basketName.toLowerCase().includes(userInput.toLowerCase())
  );

  // **New Step:** Exclude selected baskets from the sorted list
  const sortedBaskets = [...filteredBaskets]
    .filter(basket =>
      !ctx.cc.basketFilters.some(filter => filter.basketId === basket.basketId)
    )
    .sort((a, b) => {
      if (currentSort === 'alpha') {
        return a.basketName.localeCompare(b.basketName);
      } else if (currentSort === 'count') {
        return b.count - a.count; // Descending order by count
      }
      return 0;
    });

  useEffect(() => {
    if (user?.baskets) {
      const processedBaskets: BasketFacetItem[] = user.baskets.map(basket => ({
        basketId: basket.basketId,
        basketName: basket.basketName,
        count: basket.items.length,
        created: basket.created,
        altered: basket.altered
      }));
      setBasketData(processedBaskets);
    }
  }, [user?.baskets]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setUserInput(e.target.value);
  };

  const clearInput = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setUserInput('');
  };

  const handleAlphaSort = () => {
    setCurrentSort('alpha');
  };

  const handleCountSort = () => {
    setCurrentSort('count');
  };

  const handleDeleteAllSelectedBaskets = () => {
    // Clone cc object
    const newCc = { ...cc, basketFilters: [] };

    // Update cc object in context
    setCc(newCc);
    setSelectedRecord(null);
    setFirst(1);
  };

  const handleCheckboxChange = (checked: boolean, value: string) => {
    // Find the basket name from basketData using the value (basketId)
    const basket = basketData.find(b => b.basketId === value);
    if (basket) {
      if (checked) {
        ccUtils.addBasketFilter(ctx, value, basket.basketName);
      } else {
        ccUtils.removeBasketFilter(ctx, value);
      }
    }
  };

  const handleDeleteBasket = async (value: string) => {
    if (!window.confirm("Etes-vous certain de vouloir supprimer ce panier ?")) return;
    const url = `${process.env.REACT_APP_USERMANAGEMENT_URL}`;
    const postData = {
        action: 'deletebasket',
        basket: value,
        token: user.token
    };

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
    });

    if (!response.ok) {
        console.error("Failed to delete basket.");
        return;
    } else {
      setUser({
        ...user,
        baskets: user.baskets.filter(function( obj ) {  return obj.basketId !== value;  })
      })
      
    }
  }

  const handleCopyBasket = async (value: string) => {
    if (!window.confirm("Etes-vous certain de dupliquer ce panier ?")) return;
    const url = `${process.env.REACT_APP_USERMANAGEMENT_URL}`;
    const postData = {
        action: 'copybasket',
        basket: value,
        token: user.token
    };

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
    });

    if (!response.ok) {
        console.error("Failed to copy basket.");
        return;
    } else {
      const data: CopyBasketResponse = await response.json();
      var copiedBasket = user.baskets.find(item => item.basketId === value)
      if (data.basketId && user && copiedBasket) {
        const newBasket: Basket = {
          basketId: data.basketId,
          basketName: copiedBasket?.basketName + ' - copie',
          created: new Date().toISOString(),
          altered: new Date().toISOString(),
          items: copiedBasket?.items
        };
        setUser({
          ...user,
          baskets: [...user.baskets, newBasket] 
        })
        }
    }
  }
    
  const handleRenameBasket = async (value: string) => {
    const aName = window.prompt("Saisissez le nouveau nom de ce panier")
    if (!aName) return
    const newName: NonNullable<string> = aName
    const url = `${process.env.REACT_APP_USERMANAGEMENT_URL}`;
    const postData = {
        action: 'renamebasket',
        basket: value,
        basketName: newName,
        token: user.token 
    };

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
    });

    if (!response.ok) {
        console.error("Failed to rename basket.");
        return;
    } else {
      setUser({
        ...user,
        baskets: user.baskets.map(b => b.basketId === value ? { ...b, basketName: newName} : b) 
      })
      
    }
  }

  return (
    <div className={styles.basketFacetContainer} ref={facetRootRef} role="region" aria-labelledby={`basketFacet-header`}>
      <div className={styles.basketFacetName} id={`basketFacet-header`}>
        <span className={styles.basketFacetNameLabel}>
          Paniers
        </span>
        <span className={styles.basketFacetNameCount}>
          ({basketData.length} {basketData.length !== 1 ? "Paniers" : "Panier"})
        </span>
      </div>
      <div className={styles.header}>
        <div className={styles.inputContainer}>
          <input
            className={styles.mask}
            type="text"
            value={userInput}
            placeholder="filtrer"
            onChange={handleInput}
            aria-label="Basket filter"
          />
        </div>
        <GenericIconButton
          icon='light/times.svg'
          width='15px'
          height='15px'
          onClick={clearInput}
          aria-label="Clear input"
          fill='#f8f8f8'
          hoverBackgroundColor='#56ce67'
        />
        <div className={styles.sortButtons}>
          <GenericIconButton
            isActive={currentSort === 'alpha'}
            activeBorderColor='#ffffff'
            icon='regular/sort-alpha-down.svg'
            width='15px'
            height='15px'
            fill='#f8f8f8'
            hoverFill='#0000ff'
            hoverBackgroundColor='#56ce67'
            onClick={handleAlphaSort}
            aria-label="Sort alphabetically"
          />
          <GenericIconButton
            isActive={currentSort === 'count'}
            activeBorderColor='#ffffff'
            icon='regular/sort-amount-down.svg'
            width='15px'
            height='15px'
            fill='#f8f8f8'
            hoverFill='#0000ff'
            hoverBackgroundColor='#56ce67'
            onClick={handleCountSort}
            aria-label="Sort by count"
          />
        </div>
      </div>

      {/* Optional: If you want to show selected items above like in Facet */}
      {ctx.cc.basketFilters.length > 0 && (
        <div className={styles.selectedItems}>
          <div
            className={styles.clearAllContainer}
            onClick={() => { handleDeleteAllSelectedBaskets() }}
            aria-label="Clear all selections"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleDeleteAllSelectedBaskets();
              }
            }}
          >
            <div className={styles.clearAllIconContainer}>
              <div className={styles.clearAll} />
              <div className={styles.clearAll} />
              <div className={styles.clearAll} />
            </div>
            <div className={styles.clearAllText}>
              {t("facet_clear")}
            </div>
          </div>

          {ctx.cc.basketFilters.map((filter) => (
            <div key={filter.basketId}>
              <GenericCheckbox
                id={`basket_${filter.basketId}`}
                name={`basket_${filter.basketId}`}
                value={filter.basketId}
                checked={true}
                onChange={(checked) => handleCheckboxChange(checked, filter.basketId)}
                label={filter.basketName}
                aria-labelledby={`basket_${filter.basketId}-label`}
              />
            </div>
          ))}
        </div>
      )}

      <div className={styles.content} style={{ height: contentHeight }} role="list">
        {sortedBaskets.map((basket) => {
          const isChecked = ctx.cc.basketFilters.some(filter => filter.basketId === basket.basketId);
          return (
            <div key={basket.basketId} className={styles.basketItem} role="listitem" style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', columnGap: '10px'}}>
              <div style={{flex: 1}}>
                <GenericCheckbox
                  id={`basket_${basket.basketId}`}
                  name={`basket_${basket.basketId}`}
                  value={basket.basketId}
                  checked={isChecked}
                  onChange={(checked) => handleCheckboxChange(checked, basket.basketId)}
                  label={`${basket.basketName} (${basket.count})`}
                  aria-labelledby={`basket_${basket.basketId}-label`}
                />
              </div>
              <div style={{}}>
                <GenericIconButton 
                  activeBorderColor='#ffffff'
                  icon='regular/edit.svg'
                  width='15px'
                  height='15px'
                  fill='#f8f8f8'
                  hoverFill='#0000ff'
                  hoverBackgroundColor='#56ce67'
                  onClick={() => handleRenameBasket(basket.basketId)}
                />
              </div>
              <div style={{}}>
                <GenericIconButton 
                  activeBorderColor='#ffffff'
                  icon='regular/trash.svg'
                  width='15px'
                  height='15px'
                  fill='#f8f8f8'
                  hoverFill='#0000ff'
                  hoverBackgroundColor='#56ce67'
                  onClick={() => handleDeleteBasket(basket.basketId)}
                />
              </div>
              <div style={{}}>
                <GenericIconButton 
                  activeBorderColor='#ffffff'
                  icon='light/copy.svg'
                  width='15px'
                  height='15px'
                  fill='#f8f8f8'
                  hoverFill='#0000ff'
                  hoverBackgroundColor='#56ce67'
                  onClick={() => handleCopyBasket(basket.basketId)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BasketFacet;
